import { AsyncPipe, CommonModule, NgClass, NgTemplateOutlet } from '@angular/common'
import { Component, inject, Input } from '@angular/core'
import type { Params } from '@angular/router'
import { Router } from '@angular/router'
import { UntilDestroy } from '@ngneat/until-destroy'

import { insertIf } from '@app/core/insert-if'
import { truthy } from '@app/core/types/truthy'
import * as environment from '@app/environment'
import { AuthStore } from '@app/modules/auth/store/auth.store'
import { PrivacyComponent } from '@app/modules/docs/components/privacy/privacy.component'
import { TermsComponent } from '@app/modules/docs/components/terms/terms.component'
import { LinkComponent } from '@app/modules/routing/components/link/link.component'
import { SignupService } from '@app/modules/signup/services/signup.service'
import { ModalService } from '@app/overlays/modal/modal.service'
import type { FooterVariant } from '@app/services/layout.service'
import { SiteStore } from '@app/store/site.store'
import type { IconSymbol } from '@ui/components/icon/icon.model'
import { UIModule } from '@ui/ui.module'

interface FooterSocialLink {
  icon: IconSymbol
  url: string
  title: string
}

interface FooterPrimaryLink {
  url: string
  label: string
  internal: boolean
}

@UntilDestroy()
@Component({
  selector: 'sb-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['footer.component.scss'],
  imports: [AsyncPipe, NgTemplateOutlet, NgClass, UIModule, CommonModule, LinkComponent],
})
export class FooterComponent {
  readonly siteStore = inject(SiteStore)
  private readonly authStore = inject(AuthStore)
  private readonly modalService = inject(ModalService)
  private readonly router = inject(Router)
  private readonly signupService = inject(SignupService)

  @Input() variant: FooterVariant = 'simple'
  private readonly countryCode: string | undefined = this.getCountryCode()
  readonly nonCoreCountry = this.countryCode !== undefined
  readonly countryCodeUrlPrefix = this.countryCode ? `/${this.countryCode}` : ''
  readonly environment = environment

  year = `${new Date().getFullYear()}`
  loggedIn$ = this.authStore.loggedIn$

  readonly PRIMARY_LINKS = [
    {
      label: 'Login',
      url: `${this.countryCodeUrlPrefix}/login`,
      internal: true,
    },
    ...(environment.PAGES_SUGAR_BABY && !this.nonCoreCountry
      ? [
          {
            label: 'Sugar Baby',
            url: '/sugar-baby',
            internal: false,
          },
        ]
      : []),
    ...(environment.PAGES_SUGAR_DADDY && !this.nonCoreCountry
      ? [
          {
            label: 'Sugar Daddy',
            url: '/sugar-daddy',
            internal: false,
          },
        ]
      : []),
    ...(environment.PAGES_BLOG && !this.nonCoreCountry
      ? [
          {
            label: 'Blog',
            url: '/blog',
            internal: false,
          },
        ]
      : []),
    ...(environment.PAGES_STORIES && !this.nonCoreCountry
      ? [
          {
            label: 'Stories',
            url: '/stories',
            internal: false,
          },
        ]
      : []),
    ...insertIf(!this.nonCoreCountry, {
      label: 'FAQ',
      url: '/faq',
      internal: false,
    }),
    {
      label: 'Contact',
      url: `${this.countryCodeUrlPrefix}/contact`,
      internal: false,
    },
    ...(environment.PAGES_AFFILIATES
      ? [
          {
            label: 'Affiliates',
            url: `${this.countryCodeUrlPrefix}/affiliates`,
            internal: true,
          },
        ]
      : []),
    ...insertIf(!this.nonCoreCountry, {
      label: 'Testimonials',
      url: '/testimonials',
      internal: false,
    }),
    ...(environment.PAGES_GLOSSARY && !this.nonCoreCountry
      ? [
          {
            label: 'Glossary',
            url: '/glossary',
            internal: false,
          },
        ]
      : []),
    ...(environment.PAGES_SITEMAP && !this.nonCoreCountry
      ? [
          {
            label: 'Sitemap',
            url: '/sitemap',
            internal: false,
          },
        ]
      : []),
  ] as const satisfies FooterPrimaryLink[]

  readonly SOCIALS_LINKS = [
    ...insertIf(truthy(environment.instagram), {
      icon: 'ig-2',
      url: environment.instagram!,
      title: 'instagram',
    }),
    ...insertIf(truthy(environment.facebook), {
      icon: 'fb-2',
      url: environment.facebook!,
      title: 'facebook',
    }),
    ...insertIf(truthy(environment.twitter), {
      icon: 'twitter',
      url: environment.twitter!,
      title: 'x',
    }),
  ] as const satisfies FooterSocialLink[]

  private getCountryCode(): string | undefined {
    let params: Params = {}
    let route = this.router.routerState.root
    while (route.firstChild) {
      route = route.firstChild
      params = { ...params, ...route.snapshot.params }
    }
    return params.cc
  }

  onSignupClick(): void {
    this.signupService.showGenderPicker()
  }

  openPrivacyModal(): void {
    this.modalService.create(PrivacyComponent, {
      closeActionStyle: 'button',
      desktopWidth: '600px',
    }).instance.compact = true
  }

  openTermsModal(): void {
    const { instance } = this.modalService.create(TermsComponent, {
      closeActionStyle: 'button',
      desktopWidth: '600px',
    })
    instance.compact = true
  }
}
